import { FormField, FormService } from "@shared/services/FormServices";
import { DynamicForm } from "@shared/components/DynamicForm";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { Badge } from "@shared/ui/badge";
import { Button } from "@shared/ui/button";
import {
  BriefcaseBusiness,
  Plus,
  Building2,
  Trash2,
  ShieldCheck,
} from "lucide-react";
import React, { useState } from "react";
import { FIELD_CLASSES } from "@shared/utils/constants";
import {
  useChoices,
  useCreateBank,
  useBanks,
  useDeleteBank,
} from "@features/cx/paymentsv2/hooks";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";

interface ManageBanksModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const ManageBanksModal: React.FC<ManageBanksModalProps> = ({
  isOpen,
  onClose,
}) => {
  const remoteUtilityId = useRemoteUtilityId();
  const { data: accountTypeChoices } = useChoices("account_type");
  const { data: banksResponse } = useBanks(Number(remoteUtilityId), isOpen);
  const { mutate: createBank, isPending: isCreatingBank } = useCreateBank();
  const { mutate: deleteBank, isPending: isDeletingBank } = useDeleteBank();

  const banks = banksResponse?.results;

  const accountTypeOptions = React.useMemo(() => {
    if (!accountTypeChoices) return [];
    return accountTypeChoices?.map((accountType: any) => ({
      label: accountType.value,
      value: accountType.key,
    }));
  }, [accountTypeChoices]);

  const formFields: FormField[] = [
    {
      name: "bankName",
      label: "Bank Name",
      type: "text",
      required: true,
      placeholder: "e.g. HDFC Bank",
      group: "+ Add New Bank",
      groupColorNeeded: false,
      className: "col-span-1",
      classes: FIELD_CLASSES,
    },
    {
      name: "accountName",
      label: "Account Name",
      type: "text",
      placeholder: "e.g. Corporate Current A/C",
      group: "+ Add New Bank",
      groupColorNeeded: false,
      className: "col-span-1",
      classes: FIELD_CLASSES,
    },
    {
      name: "routingNumber",
      label: "Routing Number",
      type: "text",
      placeholder: "e.g. 121000248",
      group: "+ Add New Bank",
      groupColorNeeded: false,
      className: "col-span-1",
      classes: FIELD_CLASSES,
    },
    {
      name: "accountType",
      label: "Account Type",
      type: "select",
      options: accountTypeOptions,
      group: "+ Add New Bank",
      groupColorNeeded: false,
      className: "col-span-1",
      classes: FIELD_CLASSES,
    },
    {
      name: "accountNumber",
      label: "Account Number",
      type: "text",
      placeholder: "e.g. 0012345678901",
      group: "+ Add New Bank",
      groupColorNeeded: false,
      className: "col-span-2",
      classes: FIELD_CLASSES,
    },
  ];

  const form = FormService.createForm({ fields: formFields });

  const handleFormSubmit = (data: any) => {
    const payload = {
      remote_utility_id: remoteUtilityId,
      bank_name: data.bankName,
      account_name: data.accountName,
      account_type: parseInt(data.accountType, 10),
      routing_number: data.routingNumber,
      account_number: data.accountNumber,
    };

    createBank(payload, {
      onSuccess: () => {
        form.reset();
      },
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[88vh] flex flex-col p-0 gap-0 rounded-2xl">
        {/* Header */}
        <DialogHeader className="px-5 py-3.5 border-b border-gray-100 flex-shrink-0">
          <DialogTitle className="flex items-center gap-2 text-sm font-medium text-gray-900">
            <div className="w-7 h-7 bg-blue-50 rounded-lg flex items-center justify-center">
              <BriefcaseBusiness className="w-3.5 h-3.5 text-blue-600" />
            </div>
            Manage Banks
          </DialogTitle>
        </DialogHeader>

        {/* Body */}
        <div className="flex flex-col flex-1 overflow-y-auto px-5 py-4 gap-4">
          {/* Saved Banks List */}
          <div>
            <p className="text-[10px] font-medium tracking-widest text-gray-400 mb-2.5 flex items-center gap-1.5">
              Saved Banks
              <span className="ml-auto text-[10px] font-medium text-gray-400 bg-gray-100 border border-gray-200 rounded-full px-2 py-0.5">
                {banks?.length}
              </span>
            </p>

            {banks?.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 text-center">
                <Building2 className="w-7 h-7 text-gray-200 mb-2" />
                <p className="text-xs text-gray-400">No banks saved yet</p>
              </div>
            ) : (
              <div className="space-y-1.5 max-h-[250px] overflow-y-auto pr-2">
                {banks?.map((bank: any) => (
                  <div
                    key={bank.id}
                    className="group flex items-center gap-3 px-3.5 py-2.5 bg-white border border-gray-100 rounded-xl hover:border-gray-200 transition-colors"
                  >
                    {/* Avatar */}
                    <div className="w-8 h-8 flex-shrink-0 bg-blue-50 text-blue-800 rounded-lg flex items-center justify-center font-medium text-[11px]">
                      {(bank?.bankName || "N/A").charAt(0).toUpperCase()}
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-gray-900 truncate">
                        {bank.bankName || "N/A"}{" "}
                        <span className="font-normal text-gray-400">
                          — {bank.accountName || "N/A"}
                        </span>
                      </p>
                      <div className="flex items-center gap-1.5 mt-0.5 text-[11px] text-gray-400">
                        <span>Routing: {bank.routingNumber || "N/A"}</span>
                        <span className="text-gray-200">·</span>
                        <span>Acc: {bank.accountNumber || "N/A"}</span>
                        <span className="text-gray-200">·</span>
                        <span>{bank?.accountTypeDisplay}</span>
                      </div>
                    </div>

                    {/* Right side */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="w-7 h-7 text-gray-400 hover:text-red-500 hover:bg-red-50 border border-transparent hover:border-red-100 rounded-md transition-all"
                        onClick={() =>
                          deleteBank({
                            bankId: bank.id,
                            remoteUtilityId: Number(remoteUtilityId),
                          })
                        }
                        disabled={isDeletingBank}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Add New Bank Form */}
          <div className="space-y-4">
            <DynamicForm
              fields={formFields}
              form={form}
              onSubmit={handleFormSubmit}
              gridColumns={2}
              responsiveColumns={{ sm: 1, md: 2 }}
              config={{
                showSubmitButton: true,
                submitButtonText: isCreatingBank ? "Adding..." : "Add Bank",
                icon: Plus,
                classes: {
                  container: "w-full",
                  buttonContainer: "col-span-2 mt-2",
                  submitButton: "w-full h-8 text-xs font-medium",
                },
              }}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-between flex-shrink-0">
          <span className="flex items-center gap-1.5 text-[11px] text-gray-400">
            <ShieldCheck className="w-3.5 h-3.5" />
            Account numbers are stored securely
          </span>
          <Button
            variant="outline"
            size="sm"
            className="h-8 px-4 text-xs"
            onClick={onClose}
          >
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
